
import React, { useState } from 'react';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import WhatsAppButton from '../../components/base/WhatsAppButton';
import BackToTopButton from '../../components/base/BackToTopButton';
import CookieBanner from '../../components/base/CookieBanner';
import SEOHead from '../../components/seo/SEOHead';

export default function Contacto() {
  const [formData, setFormData] = useState({
    nombre: '',
    email: '',
    telefono: '',
    servicio: '',
    ubicacion: '',
    mensaje: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const formDataToSend = new URLSearchParams();
      Object.entries(formData).forEach(([key, value]) => {
        formDataToSend.append(key, value);
      });

      const response = await fetch('https://readdy.ai/api/form/d31rf9gemi0jo797cf20', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formDataToSend,
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({
          nombre: '',
          email: '',
          telefono: '',
          servicio: '',
          ubicacion: '',
          mensaje: ''
        });
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const seoSchema = {
    "@context": "https://schema.org",
    "@type": "ContactPage",
    "mainEntity": {
      "@type": "LocalBusiness",
      "name": "PintoLot",
      "telephone": "+34609936590",
      "email": "info@pintolot.com.es",
      "address": {
        "@type": "PostalAddress",
        "addressLocality": "Olot",
        "addressRegion": "Girona",
        "addressCountry": "ES"
      },
      "openingHours": [
        "Mo-Fr 08:00-18:00",
        "Sa 09:00-14:00"
      ],
      "areaServed": ["Olot", "La Garrotxa", "Girona"]
    }
  };

  return (
    <>
      <SEOHead
        title="Contacto Pintura Olot | Presupuesto Gratuito PintoLot Garrotxa"
        description="Contacta con PintoLot para presupuesto gratuito de pintura en Olot y Garrotxa. Teléfono 609 93 65 90. Servicios profesionales en Girona y provincia."
        keywords="contacto pintura Olot, presupuesto pintura Garrotxa, teléfono pintores Girona, solicitar presupuesto pintura"
        canonical={`${import.meta.env.VITE_SITE_URL}/contacto`}
        ogTitle="Contacto PintoLot - Presupuesto Gratuito Pintura Olot"
        ogDescription="Solicita tu presupuesto gratuito para servicios de pintura en Olot y la Garrotxa. Te respondemos en menos de 24 horas."
        schema={seoSchema}
      />
      <Header />
      <main>
        {/* Hero Section */}
        <section className="py-20 bg-gray-50">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              Contacto para servicios de pintura en Olot y la Garrotxa
            </h1>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Ponte en contacto con nosotros para recibir un presupuesto gratuito y personalizado para tu proyecto de pintura en Girona y provincia. Te respondemos en menos de 24 horas.
            </p>
          </div>
        </section>

        {/* Contact Form & Info */}
        <section className="py-20 bg-white">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid lg:grid-cols-2 gap-12">
              {/* Contact Form */}
              <div>
                <h2 className="text-3xl font-bold text-gray-900 mb-8">
                  Solicita tu presupuesto gratuito
                </h2>
                
                <form onSubmit={handleSubmit} data-readdy-form id="contacto" className="space-y-6">
                  <div className="grid md:grid-cols-2 gap-6">
                    <div>
                      <label htmlFor="nombre" className="block text-sm font-medium text-gray-700 mb-2">
                        Nombre completo *
                      </label>
                      <input
                        type="text"
                        id="nombre"
                        name="nombre"
                        value={formData.nombre}
                        onChange={handleInputChange}
                        required
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#51b038] focus:border-transparent text-sm"
                        placeholder="Tu nombre completo"
                      />
                    </div>
                    <div>
                      <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                        Email *
                      </label>
                      <input
                        type="email"
                        id="email"
                        name="email"
                        value={formData.email}
                        onChange={handleInputChange}
                        required
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#51b038] focus:border-transparent text-sm"
                        placeholder="tu@email.com"
                      />
                    </div>
                  </div>

                  <div className="grid md:grid-cols-2 gap-6">
                    <div>
                      <label htmlFor="telefono" className="block text-sm font-medium text-gray-700 mb-2">
                        Teléfono
                      </label>
                      <input
                        type="tel"
                        id="telefono"
                        name="telefono"
                        value={formData.telefono}
                        onChange={handleInputChange}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#51b038] focus:border-transparent text-sm"
                        placeholder="609 93 65 90"
                      />
                    </div>
                    <div>
                      <label htmlFor="servicio" className="block text-sm font-medium text-gray-700 mb-2">
                        Tipo de servicio
                      </label>
                      <select
                        id="servicio"
                        name="servicio"
                        value={formData.servicio}
                        onChange={handleInputChange}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#51b038] focus:border-transparent text-sm pr-8"
                      >
                        <option value="">Selecciona un servicio</option>
                        <option value="Pintura interior vivienda">Pintura interior vivienda</option>
                        <option value="Pintura exterior fachada">Pintura exterior/fachada</option>
                        <option value="Pintura comercial oficinas">Pintura comercial/oficinas</option>
                        <option value="Pintura industrial naves">Pintura industrial/naves</option>
                        <option value="Lacados barnizados">Lacados y barnizados</option>
                        <option value="Tecnicas decorativas">Técnicas decorativas</option>
                        <option value="Tratamientos especiales">Tratamientos especiales</option>
                        <option value="Otro servicio">Otro servicio</option>
                      </select>
                    </div>
                  </div>

                  <div>
                    <label htmlFor="ubicacion" className="block text-sm font-medium text-gray-700 mb-2">
                      Ubicación del proyecto
                    </label>
                    <input
                      type="text"
                      id="ubicacion"
                      name="ubicacion"
                      value={formData.ubicacion}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#51b038] focus:border-transparent text-sm"
                      placeholder="Olot, Girona, La Garrotxa..."
                    />
                  </div>

                  <div>
                    <label htmlFor="mensaje" className="block text-sm font-medium text-gray-700 mb-2">
                      Describe tu proyecto
                    </label>
                    <textarea
                      id="mensaje"
                      name="mensaje"
                      value={formData.mensaje}
                      onChange={handleInputChange}
                      maxLength={500}
                      rows={5}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#51b038] focus:border-transparent text-sm resize-vertical"
                      placeholder="Cuéntanos los detalles de tu proyecto: superficie a pintar, colores preferidos, plazos, etc."
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      {formData.mensaje.length}/500 caracteres
                    </p>
                  </div>

                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      id="acepto"
                      required
                      className="h-4 w-4 text-[#51b038] focus:ring-[#51b038] border-gray-300 rounded"
                    />
                    <label htmlFor="acepto" className="ml-2 text-sm text-gray-600">
                      Acepto el tratamiento de mis datos para recibir información sobre servicios de pintura *
                    </label>
                  </div>

                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="w-full bg-[#51b038] hover:bg-[#7a806a] disabled:bg-gray-400 text-white px-8 py-4 rounded-lg font-semibold text-lg transition-colors cursor-pointer whitespace-nowrap"
                  >
                    {isSubmitting ? 'Enviando...' : 'Solicitar presupuesto gratuito'}
                  </button>

                  {submitStatus === 'success' && (
                    <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                      <div className="flex">
                        <i className="ri-check-circle-line text-green-400 text-xl mr-3"></i>
                        <div>
                          <h3 className="text-sm font-medium text-green-800">¡Mensaje enviado correctamente!</h3>
                          <p className="text-sm text-green-700 mt-1">
                            Te contactaremos en menos de 24 horas para ofrecerte un presupuesto personalizado.
                          </p>
                        </div>
                      </div>
                    </div>
                  )}

                  {submitStatus === 'error' && (
                    <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                      <div className="flex">
                        <i className="ri-error-warning-line text-red-400 text-xl mr-3"></i>
                        <div>
                          <h3 className="text-sm font-medium text-red-800">Error al enviar el mensaje</h3>
                          <p className="text-sm text-red-700 mt-1">
                            Por favor, inténtalo de nuevo o llámanos directamente al 609 93 65 90.
                          </p>
                        </div>
                      </div>
                    </div>
                  )}
                </form>
              </div>

              {/* Contact Information */}
              <div>
                <h2 className="text-3xl font-bold text-gray-900 mb-8">
                  Información de contacto
                </h2>

                <div className="space-y-8">
                  <div className="flex items-start">
                    <div className="w-12 h-12 bg-[#51b038] rounded-lg flex items-center justify-center mr-4 flex-shrink-0">
                      <i className="ri-phone-line text-white text-xl"></i>
                    </div>
                    <div>
                      <h3 className="text-xl font-semibold mb-2">Teléfono</h3>
                      <p className="text-gray-600 mb-2">Llámanos para consultas inmediatas</p>
                      <a href="tel:609936590" className="text-[#51b038] hover:text-[#7a806a] font-semibold text-lg cursor-pointer">
                        609 93 65 90
                      </a>
                    </div>
                  </div>

                  <div className="flex items-start">
                    <div className="w-12 h-12 bg-[#51b038] rounded-lg flex items-center justify-center mr-4 flex-shrink-0">
                      <i className="fab fa-whatsapp text-white text-xl"></i>
                    </div>
                    <div>
                      <h3 className="text-xl font-semibold mb-2">WhatsApp</h3>
                      <p className="text-gray-600 mb-2">Envíanos fotos de tu proyecto</p>
                      <a href="https://wa.me/34609936590" className="text-[#51b038] hover:text-[#7a806a] font-semibold text-lg cursor-pointer">
                        Escribir por WhatsApp
                      </a>
                    </div>
                  </div>

                  <div className="flex items-start">
                    <div className="w-12 h-12 bg-[#51b038] rounded-lg flex items-center justify-center mr-4 flex-shrink-0">
                      <i className="ri-mail-line text-white text-xl"></i>
                    </div>
                    <div>
                      <h3 className="text-xl font-semibold mb-2">Email</h3>
                      <p className="text-gray-600 mb-2">Para consultas detalladas</p>
                      <a href="mailto:info@pintolot.com.es" className="text-[#51b038] hover:text-[#7a806a] font-semibold text-lg cursor-pointer">
                        info@pintolot.com.es
                      </a>
                    </div>
                  </div>

                  <div className="flex items-start">
                    <div className="w-12 h-12 bg-[#51b038] rounded-lg flex items-center justify-center mr-4 flex-shrink-0">
                      <i className="ri-map-pin-line text-white text-xl"></i>
                    </div>
                    <div>
                      <h3 className="text-xl font-semibold mb-2">Zona de servicio</h3>
                      <p className="text-gray-600">
                        Olot y toda la comarca de la Garrotxa<br />
                        Girona y provincia<br />
                        Desplazamiento gratuito para presupuestos
                      </p>
                    </div>
                  </div>

                  <div className="flex items-start">
                    <div className="w-12 h-12 bg-[#51b038] rounded-lg flex items-center justify-center mr-4 flex-shrink-0">
                      <i className="ri-time-line text-white text-xl"></i>
                    </div>
                    <div>
                      <h3 className="text-xl font-semibold mb-2">Horario de atención</h3>
                      <p className="text-gray-600">
                        Lunes a Viernes: 8:00 - 18:00<br />
                        Sábados: 9:00 - 14:00<br />
                        Disponibles para emergencias
                      </p>
                    </div>
                  </div>
                </div>

                {/* Map */}
                <div className="mt-12">
                  <h3 className="text-xl font-semibold mb-4">Nuestra ubicación</h3>
                  <div className="bg-gray-100 rounded-lg overflow-hidden">
                    <iframe
                      src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d11873.534567890123!2d2.4829!3d42.1816!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x12a5084e4d6c9b5f%3A0x4d4b8e4f1e1a1a1a!2sOlot%2C%20Girona!5e0!3m2!1ses!2ses!4v1640995200000!5m2!1ses!2ses"
                      width="100%"
                      height="300"
                      style={{ border: 0 }}
                      allowFullScreen
                      loading="lazy"
                      referrerPolicy="no-referrer-when-downgrade"
                      title="Ubicación PintoLot en Olot"
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Why Contact Us */}
        <section className="py-20 bg-gray-50">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
                ¿Por qué contactar con PINTOLOT?
              </h2>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                Somos la empresa de confianza para servicios de pintura en Olot y la Garrotxa. Te ofrecemos las mejores garantías y un servicio profesional excepcional.
              </p>
            </div>

            <div className="grid md:grid-cols-3 gap-8">
              <div className="bg-white p-8 rounded-lg shadow-lg text-center">
                <div className="w-16 h-16 bg-[#51b038] rounded-lg flex items-center justify-center mb-6 mx-auto">
                  <i className="ri-money-dollar-circle-line text-2xl text-white"></i>
                </div>
                <h3 className="text-xl font-semibold mb-4">Presupuesto gratuito</h3>
                <p className="text-gray-600">
                  Visitamos tu proyecto y te ofrecemos un presupuesto detallado sin coste alguno. Sin letra pequeña ni sorpresas.
                </p>
              </div>

              <div className="bg-white p-8 rounded-lg shadow-lg text-center">
                <div className="w-16 h-16 bg-[#51b038] rounded-lg flex items-center justify-center mb-6 mx-auto">
                  <i className="ri-customer-service-2-line text-2xl text-white"></i>
                </div>
                <h3 className="text-xl font-semibold mb-4">Atención personalizada</h3>
                <p className="text-gray-600">
                  Cada proyecto es único. Te asesoramos personalmente para encontrar la mejor solución para tus necesidades específicas.
                </p>
              </div>

              <div className="bg-white p-8 rounded-lg shadow-lg text-center">
                <div className="w-16 h-16 bg-[#51b038] rounded-lg flex items-center justify-center mb-6 mx-auto">
                  <i className="ri-time-line text-2xl text-white"></i>
                </div>
                <h3 className="text-xl font-semibold mb-4">Respuesta rápida</h3>
                <p className="text-gray-600">
                  Te contactamos en menos de 24 horas. Valoramos tu tiempo y nos comprometemos con la puntualidad en cada fase del proyecto.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Service Areas */}
        <section className="py-20 bg-white">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
                Zonas donde ofrecemos nuestros servicios
              </h2>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                Prestamos servicios de pintura profesional en Olot y nos desplazamos por toda la comarca de la Garrotxa y la provincia de Girona para atender tu proyecto.
              </p>
            </div>

            <div className="grid md:grid-cols-4 gap-6">
              {[
                'Olot', 'Besalú', 'Castellfollit de la Roca', 'Sant Joan les Fonts',
                'Santa Pau', 'Les Preses', 'Montagut i Oix', 'Sant Feliu de Pallerols',
                'Girona', 'Figueres', 'Blanes', 'Sant Feliu de Guíxols',
                'Lloret de Mar', 'Ripoll', 'Puigcerdà', 'La Bisbal d\'Empordà'
              ].map((city) => (
                <div key={city} className="bg-gray-50 rounded-lg p-4 text-center hover:bg-[#51b038] hover:text-white transition-colors group">
                  <i className="ri-map-pin-line text-2xl text-[#51b038] group-hover:text-white mb-2"></i>
                  <h3 className="font-semibold">{city}</h3>
                </div>
              ))}
            </div>

            <div className="text-center mt-8">
              <p className="text-gray-600">
                ¿Tu localidad no aparece en la lista? 
                <a href="tel:609936590" className="text-[#51b038] hover:text-[#7a806a] font-semibold cursor-pointer ml-1">
                  Llámanos
                </a> 
                y te confirmamos si podemos desplazarnos.
              </p>
            </div>
          </div>
        </section>

        {/* Emergency Contact */}
        <section className="py-20 bg-[#51b038]">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
              ¿Necesitas un servicio urgente?
            </h2>
            <p className="text-xl text-green-100 mb-8 max-w-2xl mx-auto">
              Para situaciones urgentes como humedades, filtraciones o problemas que requieran atención inmediata, estamos disponibles para servicios de emergencia.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a 
                href="tel:609936590" 
                className="bg-white text-[#51b038] hover:bg-gray-100 px-8 py-4 rounded-lg font-semibold text-lg transition-colors cursor-pointer whitespace-nowrap"
              >
                <i className="ri-phone-line mr-2"></i>
                Llamar ahora: 609 93 65 90
              </a>
              <a 
                href="https://wa.me/34609936590" 
                className="border-2 border-white text-white hover:bg-white hover:text-[#51b038] px-8 py-4 rounded-lg font-semibold text-lg transition-colors cursor-pointer whitespace-nowrap"
              >
                <i className="fab fa-whatsapp mr-2"></i>
                WhatsApp emergencias
              </a>
            </div>
          </div>
        </section>
      </main>
      <Footer />
      <WhatsAppButton />
      <BackToTopButton />
      <CookieBanner />
    </>
  );
}
